import React from 'react';
import PropTypes from 'prop-types';

import {
  Color,
  ColorSchema,
  colorSchemaList,
  COLOR_SCHEMA_DARK,
  COLOR_SCHEMA_DARK_LIGHT,
  COLOR_SCHEMA_LIGHT,
} from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { iif } from '../../../utils/object';
import { getMappedColor, makeClassNames, IClassNames, applyColorSchema } from '../../../utils/styles';
import { dateFormat, formats as DateFormatType, formatsList as dateFormatsList } from '../../../utils/times';
import Text from '../text/text';

import './match-data-row.scss';

export interface IMatchDataRow extends IComponent {
  anchor?: string;
  date?: string;
  title?: string;
  roundName?: string | null;
  dateTimeFormatLong?: DateFormatType;
  dateTimeFormatShort?: DateFormatType;
  isMobileDateShort?: boolean;
}

const DEFAULT_COLOR: Color = 'blue';
const COLOR_WITH_SCHEMA_DARK: Color = 'light';
const COLOR_WITH_SCHEMA_LIGHT: Color = 'blue';

const getColorSchemaMapping = () => {
  return {
    [COLOR_SCHEMA_DARK]: COLOR_WITH_SCHEMA_DARK,
    [COLOR_SCHEMA_DARK_LIGHT]: COLOR_WITH_SCHEMA_DARK,
    [COLOR_SCHEMA_LIGHT]: COLOR_WITH_SCHEMA_LIGHT,
  };
};

const classNames = (colorSchema: ColorSchema): IClassNames =>
  makeClassNames({
    matchDataRow: ['match-data-row', `${applyColorSchema('match-data-row', colorSchema)}`],
    matchDataRowTd: [`match-data-row__td`, `${applyColorSchema('match-data-row__td', colorSchema)}`],
    matchDataRowTitle: [`match-data-row__title`, `${applyColorSchema('match-data-row__title', colorSchema)}`],
    matchDataRowDateLong: [`match-data-row__date-long`, `${applyColorSchema('match-data-row__date', colorSchema)}`],
    matchDataRowDateShort: [`match-data-row__date-short`, `${applyColorSchema('match-data-row__date', colorSchema)}`],
  });

/**
 * MatchDataRow will output a &lt;tr&gt;
 */
const MatchDataRow: React.FC<IMatchDataRow> = ({
  anchor,
  date,
  title,
  colorSchema,
  roundName,
  isMobileDateShort = true,
  /* istanbul ignore next: default value */ dateTimeFormatLong = 'dddd D MMMM YYYY',
  /* istanbul ignore next: default value */ dateTimeFormatShort = 'ddd D MMM',
}) => {
  const anchorProps = anchor ? { id: anchor } : {};
  const colorSchemaMapping = getColorSchemaMapping();
  const color = getMappedColor({ colorSchemaMapping, colorSchema, defaultColor: DEFAULT_COLOR });
  const cn = classNames(colorSchema || COLOR_SCHEMA_LIGHT);
  const titleSize = iif(roundName, '14--16', colorSchema === COLOR_SCHEMA_LIGHT ? '14' : '16--14');
  const roundString = roundName ? `${roundName} ` : '';
  const dateFormattedLong = dateFormat({ date, format: dateTimeFormatLong });
  const dateStringLong = `${roundString}${dateFormattedLong.replace(/(^\w{1})|(\s{1}\w{1})/g, (match) =>
    match.toUpperCase()
  )}`;
  const dateFormattedShort = dateFormat({ date, format: dateTimeFormatShort });
  const dateStringShort = `${roundString}${dateFormattedShort.replace(/(^\w{1})|(\s{1}\w{1})/g, (match) =>
    match.toUpperCase()
  )}`;

  return (
    <tr className={cn.matchDataRow} {...anchorProps}>
      {isMobileDateShort ? (
        <td className={cn.matchDataRowTd} colSpan={100}>
          {title && (
            <Text className={cn.matchDataRowTitle} tag="h3" color={color} fontSize={titleSize} fontWeight="500">
              {title}
            </Text>
          )}
          {date && dateStringLong && (
            <Text className={cn.matchDataRowDateLong} color={color} fontSize="14--16">
              {dateStringLong}
            </Text>
          )}
          {date && dateStringShort && (
            <Text className={cn.matchDataRowDateShort} color={color} fontSize="14">
              {dateStringShort}
            </Text>
          )}
        </td>
      ) : (
        <td className={cn.matchDataRowTd} colSpan={100}>
          {title && date && dateStringLong && (
            <Text className={cn.matchDataRowDateLong} color={color} fontSize="14">
              {title} - {dateStringLong}
            </Text>
          )}
        </td>
      )}
    </tr>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  MatchDataRow.propTypes = {
    anchor: PropTypes.string,
    colorSchema: PropTypes.oneOf(colorSchemaList),
    date: PropTypes.string,
    dateTimeFormatLong: PropTypes.oneOf(dateFormatsList),
    dateTimeFormatShort: PropTypes.oneOf(dateFormatsList),
    roundName: PropTypes.string,
    title: PropTypes.string,
  };
}

export default MatchDataRow;
